import { useState, useEffect, useRef } from 'react'
import { useModalDeepLink } from '@afterpaytouch/core'
import { stringToBoolean } from '@afterpay/utils/string'
import { useGetBankAccountsQuery } from '../../state'
import { achOauthFlowSessionStorageInstance } from './BasePlaid'
import { AchFlowSteps, AchStepsProps } from './AchFlowModalContext'
import { createModalHook, useSearchParam } from '../../hooks'
import { SessionStorageActionType } from '../../utils/sessionStorageHelper'
import { ACH_FLOW_INITIAL_STEP } from '../../config/plaid'

interface UseACHModalReturn<T> {
  achFlowInitialStep: AchStepsProps
  setAchFlowInitiateStep: React.Dispatch<React.SetStateAction<AchStepsProps>>
  isAchFlowModalOpen: boolean
  openAchFlowModal: () => void
  closeAchFlowModal: () => void
  bankData: any
  isLoadingBankData: boolean
  isFetchingBankData: boolean
  isBankError: boolean
  refetchBank: () => void
  isAchSuccess: boolean
  setIsAchSuccess: React.Dispatch<React.SetStateAction<boolean>>
  segmentValue: string | number | T
  setSegmentValue: React.Dispatch<React.SetStateAction<string | number | T>>
}

export const useACHModal = <T>(
  flag: boolean = true,
  achFlowModalName: string = 'add-bank-account',
  defaultSegmentValue: string | number | T = 0
): UseACHModalReturn<T> => {
  // @ts-ignore: OPERATION BLEED STOPPER
  const isACHFlowModalOpenParam = stringToBoolean(useSearchParam(`modal-${achFlowModalName}`))
  const achFlowRedirectStep = isACHFlowModalOpenParam ? achOauthFlowSessionStorageInstance.getSessionStorageByItemKey(ACH_FLOW_INITIAL_STEP) : null
  const [isAchSuccess, setIsAchSuccess] = useState(false)
  const [segmentValue, setSegmentValue] = useState<string | number | T>(defaultSegmentValue)
  const [achFlowInitialStep, setAchFlowInitiateStep] = useState<AchStepsProps>((achFlowRedirectStep as AchStepsProps) ?? AchFlowSteps.SelectPaymentMethodType)
  const [isAchFlowModalOpen, { openModal: openAchFlowModal, closeModal: closeAchFlowModal }] = createModalHook()(isACHFlowModalOpenParam)
  useModalDeepLink(isAchFlowModalOpen, achFlowModalName)
  const isFirstRender = useRef(true)

  const {
    data: bankData,
    isLoading: isLoadingBankData,
    isFetching: isFetchingBankData,
    isError: isBankError,
    refetch: refetchBank,
  } = useGetBankAccountsQuery(undefined, { skip: !flag })

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (isAchSuccess && !isAchFlowModalOpen && flag) {
      refetchBank()
      setAchFlowInitiateStep(AchFlowSteps.SelectPaymentMethodType)
      setSegmentValue(defaultSegmentValue)
    }
    if (!isAchFlowModalOpen) {
      achOauthFlowSessionStorageInstance.handleSessionStorage(SessionStorageActionType.REMOVE)
      setAchFlowInitiateStep(AchFlowSteps.SelectPaymentMethodType)
    }
  }, [isAchSuccess, isAchFlowModalOpen, refetchBank])

  return {
    achFlowInitialStep,
    setAchFlowInitiateStep,
    isAchFlowModalOpen,
    openAchFlowModal,
    closeAchFlowModal,
    bankData,
    isLoadingBankData,
    isFetchingBankData,
    isBankError,
    refetchBank,
    isAchSuccess,
    setIsAchSuccess,
    segmentValue,
    setSegmentValue,
  }
}
